// Workflow status metadata — keeps labels, colours and ordering in one place.
// Mirrors backend OrderWorkflowStatus enum. See docs/ORDER_WORKFLOW.md.

export const WORKFLOW_CHAIN = [
  { key: 'DRAFT', label: 'Draft', short: 'Draft', tone: 'slate' },
  { key: 'APPROVED', label: 'Approved', short: 'Approved', tone: 'blue' },
  { key: 'PLACE_ORDER', label: 'Place order', short: 'Place', tone: 'blue' },
  { key: 'IN_PRODUCTION', label: 'In production', short: 'Production', tone: 'teal' },
  { key: 'PRODUCTION_DONE', label: 'Completed', short: 'Completed', tone: 'teal' },
  { key: 'BUTTA_CUTTING', label: 'Butta cutting', short: 'Butta', tone: 'indigo' },
  { key: 'MILL_FINISH', label: 'Mill finish', short: 'Mill', tone: 'violet' },
  { key: 'BORDER_CUTTING', label: 'Border cutting', short: 'Border', tone: 'pink' },
  { key: 'HANDWORK', label: 'Handwork', short: 'Handwork', tone: 'pink' },
  { key: 'READY_FOR_DISPATCH', label: 'Ready for dispatch', short: 'Ready', tone: 'emerald' },
  { key: 'IN_STOCK', label: 'In stock', short: 'Stock', tone: 'sky' },
  { key: 'DISPATCHED', label: 'Dispatched', short: 'Dispatched', tone: 'emerald' },
  { key: 'DELIVERED', label: 'Delivered', short: 'Delivered', tone: 'emerald' },
  { key: 'CANCELLED', label: 'Cancelled', short: 'Cancelled', tone: 'rose' },
];

// Legacy / deprecated → canonical mapping. Mirrors backend canonical().
const LEGACY_MAP = {
  PENDING: 'DRAFT',
  PENDING_APPROVAL: 'DRAFT',
  APPROVE: 'APPROVED',
  PRODUCTION: 'IN_PRODUCTION',
  CHECKING: 'PRODUCTION_DONE',
  IN_CHECKING: 'PRODUCTION_DONE',
  CHECKING_DONE: 'PRODUCTION_DONE',
  IN_BLEACHING: 'BUTTA_CUTTING',
  BLEACHED_AND_RECEIVED: 'BUTTA_CUTTING',
  IN_FINISHING: 'MILL_FINISH',
  FINISHED_AND_RECEIVED: 'MILL_FINISH',
  IN_HANDWORK: 'HANDWORK',
  RECEIVED_FROM_HANDWORK: 'HANDWORK',
  COMPLETED: 'DELIVERED',
};

export function canonicalStatus(key) {
  if (!key) return 'DRAFT';
  return LEGACY_MAP[key] || key;
}

export function statusMeta(key) {
  const k = canonicalStatus(key);
  return WORKFLOW_CHAIN.find((s) => s.key === k) || { key: k, label: k, short: k, tone: 'slate' };
}

export function statusLabel(key) {
  return statusMeta(key).label;
}

// Tone → classes (background + text).
export const TONE_BG = {
  slate: 'bg-slate-100 text-slate-700 ring-slate-200',
  amber: 'bg-amber-100 text-amber-800 ring-amber-200',
  blue: 'bg-blue-100 text-blue-800 ring-blue-200',
  teal: 'bg-teal-100 text-teal-800 ring-teal-200',
  sky: 'bg-sky-100 text-sky-800 ring-sky-200',
  indigo: 'bg-indigo-100 text-indigo-800 ring-indigo-200',
  violet: 'bg-violet-100 text-violet-800 ring-violet-200',
  pink: 'bg-pink-100 text-pink-800 ring-pink-200',
  emerald: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
  rose: 'bg-rose-100 text-rose-800 ring-rose-200',
};

export const TONE_DOT = {
  slate: 'bg-slate-400',
  amber: 'bg-amber-500',
  blue: 'bg-blue-500',
  teal: 'bg-teal-500',
  sky: 'bg-sky-500',
  indigo: 'bg-indigo-500',
  violet: 'bg-violet-500',
  pink: 'bg-pink-500',
  emerald: 'bg-emerald-500',
  rose: 'bg-rose-500',
};

/** Linear production + fulfilment only — vendor work lives in Post-production trips. */
export const PRODUCTION_TIMELINE_STAGES = [
  { key: 'APPROVED', label: 'Approved', circleClass: 'bg-blue-600' },
  { key: 'PLACE_ORDER', label: 'Place order', circleClass: 'bg-amber-500' },
  { key: 'IN_PRODUCTION', label: 'Production', circleClass: 'bg-teal-600' },
  { key: 'PRODUCTION_DONE', label: 'Completed', circleClass: 'bg-teal-700' },
  { key: 'READY_FOR_DISPATCH', label: 'Ready', circleClass: 'bg-emerald-600' },
  { key: 'DISPATCHED', label: 'Dispatched', circleClass: 'bg-emerald-700' },
  { key: 'DELIVERED', label: 'Delivered', circleClass: 'bg-slate-700' },
];

/** Vendor trip chain node colour (matches stage family). */
const VENDOR_TRIP_NODE_CLASS = {
  BUTTA_CUTTING: 'bg-indigo-600',
  MILL_FINISH: 'bg-violet-600',
  BORDER_CUTTING: 'bg-pink-600',
  HANDWORK: 'bg-rose-600',
};

export function vendorTripNodeClass(stageKey) {
  const k = canonicalStatus(stageKey);
  return VENDOR_TRIP_NODE_CLASS[k] || 'bg-slate-600';
}

const VENDOR_STATUS_SET = new Set(['BUTTA_CUTTING', 'MILL_FINISH', 'BORDER_CUTTING', 'HANDWORK']);

const LEGACY_VENDOR_STAGE_MAP = {
  BLEACHING: 'BUTTA_CUTTING',
  FINISHING: 'MILL_FINISH',
  BORDER_CUTTING: 'BORDER_CUTTING',
  HANDWORK: 'HANDWORK',
};

/** Parse vendor stage from VENDOR_SEND / VENDOR_RECEIVE activity context JSON. */
export function parseVendorStageFromContext(contextJson) {
  if (!contextJson) return null;
  try {
    const obj = JSON.parse(contextJson);
    const raw = String(obj?.stage || '').trim();
    if (VENDOR_STATUS_SET.has(raw)) return raw;
    const mapped = LEGACY_VENDOR_STAGE_MAP[raw.toUpperCase()];
    return mapped || null;
  } catch {
    return null;
  }
}

/**
 * completedThrough = inclusive index of last milestone showing ✓ (not yet reached => -1 for before Approved).
 * activeIndex = step ring highlight / “you are here” toward next work.
 */
export function productionTimelineProgress(status) {
  const s = canonicalStatus(status);
  if (s === 'DRAFT' || s === 'CANCELLED') {
    return { completedThrough: -1, activeIndex: -1 };
  }
  const keys = PRODUCTION_TIMELINE_STAGES.map((x) => x.key);
  const last = keys.length - 1;

  if (VENDOR_STATUS_SET.has(s)) {
    return { completedThrough: 3, activeIndex: 4 };
  }
  if (s === 'IN_STOCK') {
    return { completedThrough: 4, activeIndex: 5 };
  }

  const idx = keys.indexOf(s);
  if (idx >= 0) {
    if (s === 'DELIVERED') return { completedThrough: last, activeIndex: last };
    if (s === 'APPROVED') return { completedThrough: -1, activeIndex: 0 };
    if (s === 'PLACE_ORDER') return { completedThrough: 0, activeIndex: 1 };
    if (s === 'IN_PRODUCTION') return { completedThrough: 1, activeIndex: 2 };
    if (s === 'PRODUCTION_DONE') return { completedThrough: 3, activeIndex: 4 };
    if (s === 'READY_FOR_DISPATCH') return { completedThrough: 4, activeIndex: 5 };
    if (s === 'DISPATCHED') return { completedThrough: 5, activeIndex: 6 };
  }
  return { completedThrough: -1, activeIndex: 0 };
}

/** Extract milestone timestamps (activities from API are newest-first → reverse for chronological). */
export function extractProductionStageDates(activities = []) {
  const dates = {};
  const chronological = [...(activities || [])].reverse();
  for (const a of chronological) {
    const t = a?.eventType;
    const to = canonicalStatus(a?.toStatus);
    if (t === 'ORDER_STATUS_CHANGE' && to && dates[to] == null) {
      dates[to] = a.createdAt;
    }
    if (t === 'APPROVE' && dates.APPROVED == null) {
      dates.APPROVED = a.createdAt;
    }
    if (t === 'DISPATCH' && dates.DISPATCHED == null) {
      dates.DISPATCHED = a.createdAt;
    }
    if (t === 'DELIVER' && dates.DELIVERED == null) {
      dates.DELIVERED = a.createdAt;
    }
  }
  return dates;
}

/** Short date for trip rows / pills (exported for UI). */
export function formatShortActivityDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return String(iso);
  }
}

/**
 * Pair sends with receives FIFO per stage (chronological). Unmatched sends = active trips.
 * Activities from API are newest-first.
 */
export function buildVendorTripsFromActivities(activities = []) {
  const chronological = [...(activities || [])].reverse();
  const pending = [];
  const resolved = [];

  for (const ev of chronological) {
    if (ev.eventType === 'VENDOR_SEND') {
      const stage = parseVendorStageFromContext(ev.contextJson);
      if (!stage) continue;
      pending.push({ stage, send: ev });
      continue;
    }
    if (ev.eventType === 'VENDOR_RECEIVE') {
      const stage = parseVendorStageFromContext(ev.contextJson);
      if (!stage) continue;
      const idx = pending.findIndex((p) => p.stage === stage);
      if (idx === -1) continue;
      const item = pending.splice(idx, 1)[0];
      resolved.push({ stage: item.stage, send: item.send, receive: ev });
    }
  }

  const rows = [...resolved, ...pending.map((p) => ({ stage: p.stage, send: p.send, receive: null }))];
  rows.sort((a, b) => String(a.send?.id || '').localeCompare(String(b.send?.id || '')));

  return rows.map((row, idx) => {
    const { stage, send, receive } = row;
    let vendorName = '';
    let piecesSent = null;
    try {
      const ctx = JSON.parse(send.contextJson || '{}');
      vendorName = ctx.vendorPartyName || '';
      if (ctx.piecesSent != null) piecesSent = ctx.piecesSent;
    } catch {
      vendorName = '';
    }
    let piecesLost = null;
    let lossReason = null;
    let receivedAt = null;
    if (receive) {
      receivedAt = receive.createdAt;
      try {
        const rc = JSON.parse(receive.contextJson || '{}');
        if (rc.piecesLost != null) piecesLost = rc.piecesLost;
        if (rc.lossReason) lossReason = rc.lossReason;
      } catch {
        /* ignore */
      }
    }
    return {
      tripNo: idx + 1,
      stage,
      vendorName,
      sentAt: send.createdAt,
      receivedAt,
      piecesSent,
      piecesLost,
      lossReason,
      active: !receive,
    };
  });
}

/** Sum pieces lost on vendor receive events (from activity context). */
export function sumVendorPiecesLost(activities) {
  if (!Array.isArray(activities)) return 0;
  return activities.reduce((sum, a) => {
    if (a?.eventType !== 'VENDOR_RECEIVE') return sum;
    try {
      const ctx = JSON.parse(a.contextJson || '{}');
      const lost = Number(ctx.piecesLost);
      return sum + (Number.isFinite(lost) && lost > 0 ? lost : 0);
    } catch {
      return sum;
    }
  }, 0);
}

/** Known damage before dispatch: beam scrapped + vendor losses, unless already saved on order. */
export function resolveDispatchDamagedPieces(order, activities) {
  if (order?.damagedPieces != null && order.damagedPieces !== '') {
    const saved = Number(order.damagedPieces);
    if (Number.isFinite(saved) && saved >= 0) return saved;
  }
  const scrapped = Number(order?.beamsSummary?.totalScrappedPieces) || 0;
  const vendorLost = sumVendorPiecesLost(activities);
  return scrapped + vendorLost;
}

// ── Beam status metadata ────────────────────────────────────────────────
// Mirrors backend BeamStatus: PENDING / RUNNING / ON_HOLD / COMPLETED.
export const BEAM_STATUS_META = {
  PENDING: { label: 'Not started', tone: 'slate', dot: 'bg-slate-400' },
  RUNNING: { label: 'In progress', tone: 'teal', dot: 'bg-teal-500' },
  ON_HOLD: { label: 'Paused', tone: 'amber', dot: 'bg-amber-500' },
  COMPLETED: { label: 'Completed', tone: 'emerald', dot: 'bg-emerald-500' },
};

/** Legacy rows or cached payloads may still use DONE; treat as COMPLETED. */
export function beamStatusMeta(key) {
  const k = key === 'DONE' ? 'COMPLETED' : key;
  return BEAM_STATUS_META[k] || BEAM_STATUS_META.PENDING;
}

export function isBeamTerminalStatus(status) {
  return status === 'COMPLETED' || status === 'DONE';
}

// Hold reasons (order-level hold flag).
export const HOLD_REASON_LABEL = {
  AWAITING_PARTY_APPROVAL: 'Awaiting party approval',
  MATERIAL_SHORTAGE: 'Material shortage',
  MACHINE_BREAKDOWN: 'Machine breakdown',
  VENDOR_DELAY: 'Vendor delay',
  OTHER: 'Other',
};

// Simple loss-reason labels shown in vendor receive & beam complete modals.
export const LOSS_REASON_LABEL = {
  THREAD_BREAK: 'Thread break / stitching issue',
  FABRIC_DEFECT: 'Fabric defect',
  MACHINE_FAULT: 'Machine fault',
  DAMAGED_AT_VENDOR: 'Damaged at vendor',
  LOST_AT_VENDOR: 'Lost at vendor',
  QUALITY_REJECT: 'Quality reject',
  SHORTFALL: 'Shortfall / missing (not found)',
  OTHER: 'Other',
};

export const PRODUCTION_LOSS_REASONS = [
  'THREAD_BREAK', 'SHORTFALL', 'FABRIC_DEFECT', 'MACHINE_FAULT', 'QUALITY_REJECT', 'OTHER',
];

export const VENDOR_LOSS_REASONS = [
  'DAMAGED_AT_VENDOR', 'LOST_AT_VENDOR', 'QUALITY_REJECT', 'OTHER',
];

